// app/utils/geocoding.ts
// Fonctions pures de géocodage — auto-importées par Nuxt

import type {
  EstablishmentSearchBBox,
  EstablishmentSearchGeoLabels,
} from '~/types/EstablishmentSearch'
import type { SchoolAddressObj } from '~/types/school'

export interface GeoCoords {
  lat: number
  lng: number
}

const CLOSEST_CITY_CACHE_KEY = 'landing.closestCity'
const CLOSEST_CITY_CACHE_TTL_MS = 24 * 60 * 60 * 1000

/**
 * Coordonnées des grandes villes étudiantes françaises.
 * Clés = nom normalisé (`normalizeCityName`). Sert à trouver la ville étudiante la plus proche.
 */
const STUDENT_CITY_COORDS: Record<string, GeoCoords> = {
  paris: { lat: 48.8566, lng: 2.3522 },
  lyon: { lat: 45.764, lng: 4.8357 },
  marseille: { lat: 43.2965, lng: 5.3698 },
  lille: { lat: 50.6292, lng: 3.0573 },
  rennes: { lat: 48.1173, lng: -1.6778 },
  toulouse: { lat: 43.6047, lng: 1.4442 },
  montpellier: { lat: 43.6111, lng: 3.8767 },
  bordeaux: { lat: 44.8378, lng: -0.5792 },
  nantes: { lat: 47.2184, lng: -1.5536 },
  strasbourg: { lat: 48.5734, lng: 7.7521 },
  rouen: { lat: 49.4431, lng: 1.0993 },
  brest: { lat: 48.3904, lng: -4.4861 },
  angers: { lat: 47.4784, lng: -0.5632 },
  grenoble: { lat: 45.1885, lng: 5.7245 },
  dijon: { lat: 47.322, lng: 5.0415 },
  'clermont-ferrand': { lat: 45.7772, lng: 3.087 },
  tours: { lat: 47.3941, lng: 0.6848 },
  poitiers: { lat: 46.5802, lng: 0.3404 },
  nice: { lat: 43.7102, lng: 7.262 },
  'aix-en-provence': { lat: 43.5297, lng: 5.4474 },
  amiens: { lat: 49.8941, lng: 2.2958 },
  besancon: { lat: 47.2378, lng: 6.0241 },
  caen: { lat: 49.1829, lng: -0.3707 },
  'le mans': { lat: 48.0061, lng: 0.1996 },
  metz: { lat: 49.1193, lng: 6.1757 },
  nancy: { lat: 48.6921, lng: 6.1844 },
  orleans: { lat: 47.9029, lng: 1.9093 },
  reims: { lat: 49.2583, lng: 4.0317 },
  'saint-etienne': { lat: 45.4397, lng: 4.3872 },
  toulon: { lat: 43.1242, lng: 5.928 },
  avignon: { lat: 43.9493, lng: 4.8055 },
  pau: { lat: 43.2951, lng: -0.3708 },
  perpignan: { lat: 42.6886, lng: 2.8948 },
  limoges: { lat: 45.8354, lng: 1.2645 },
  mulhouse: { lat: 47.7508, lng: 7.3359 },
  valence: { lat: 44.9334, lng: 4.8924 },
  chambery: { lat: 45.5646, lng: 5.9178 },
  annecy: { lat: 45.8992, lng: 6.1294 },
  'la rochelle': { lat: 46.1603, lng: -1.1511 },
}

interface CachedClosestCity {
  city: string
  timestamp: number
}

/**
 * Géocode une adresse via Nominatim (OpenStreetMap).
 * Accepte soit une adresse décomposée, soit un SchoolAddressObj.
 *
 * ⚠️ Nominatim limite à ~1 req/s — à utiliser avec parcimonie côté client.
 * @param {string | SchoolAddressObj} address Adresse complète (string) ou objet adresse.
 * @param {string} [zipcode] Code postal (si `address` est une string).
 * @param {string} [city] Ville (si `address` est une string).
 * @returns {Promise<GeoCoords | undefined>} Coordonnées trouvées, sinon `undefined`.
 */
export const geocodeAddress = async (
  address: string | SchoolAddressObj,
  zipcode?: string,
  city?: string,
): Promise<GeoCoords | undefined> => {
  // Si on reçoit un objet SchoolAddressObj, on extrait les champs
  const fullAddress =
    typeof address === 'string'
      ? `${address}, ${zipcode ?? ''} ${city ?? ''}`.trim()
      : `${address.Address}, ${address.ZipCode} ${address.City}`.trim()

  if (!fullAddress) return undefined

  try {
    const response = await fetch(
      `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(fullAddress)}`,
    )
    const results = await response.json()

    if (Array.isArray(results) && results.length > 0) {
      return {
        lat: parseFloat(results[0].lat),
        lng: parseFloat(results[0].lon),
      }
    }
    return undefined
  } catch (error) {
    console.error('[geocoding] Géocodage échoué pour :', fullAddress, error)
    return undefined
  }
}

/**
 * Extrait les coordonnées d'une adresse : pré-stockées si présentes,
 * sinon géocodage Nominatim en fallback.
 * @param {SchoolAddressObj} address Adresse école (potentiellement avec lat/lng).
 * @returns {Promise<GeoCoords | undefined>} Coordonnées trouvées, sinon `undefined`.
 */
export const getAddressCoordinates = async (
  address: SchoolAddressObj,
): Promise<GeoCoords | undefined> => {
  if (typeof address.Latitude === 'number' && typeof address.Longitude === 'number') {
    return { lat: address.Latitude, lng: address.Longitude }
  }
  return geocodeAddress(address)
}

/**
 * Position approximative de l'utilisateur : IP (silencieux) puis géoloc navigateur en fallback.
 * @returns {Promise<GeoCoords | undefined>} Coordonnées ou `undefined`.
 */
export const getUserCoordinates = async (): Promise<GeoCoords | undefined> => {
  if (!import.meta.client) return undefined
  return (await getIpCoordinates()) ?? (await getBrowserCoordinates())
}

/**
 * Retourne la grande ville étudiante la plus proche de l'utilisateur.
 * Stratégie : cache session → IP (silencieux) → géoloc navigateur.
 * La recherche porte sur toutes les entrées de `STUDENT_CITY_COORDS`, pas seulement les chips API.
 * @returns {Promise<string | undefined>} Libellé de la ville étudiante la plus proche ou `undefined`.
 */
export const getClosestStudentCity = async (): Promise<string | undefined> => {
  if (!import.meta.client) return undefined

  const cachedCity: string | undefined = getCachedClosestStudentCity()
  if (cachedCity) return cachedCity

  const userCoords: GeoCoords | undefined = await getUserCoordinates()
  if (!userCoords) return undefined

  const closestCity: string | undefined = findClosestStudentCity(userCoords)
  if (!closestCity) return undefined

  setCachedClosestStudentCity(closestCity)
  return closestCity
}

/**
 * @param {string} value Nom de ville.
 * @returns {string} Forme normalisée (minuscule + sans accents).
 */
function normalizeCityName(value: string): string {
  return value
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase()
    .trim()
}

/**
 * @param {string} cityKey Clé normalisée (`rennes`, `aix-en-provence`…).
 * @returns {string} Libellé affichable (`Rennes`, `Aix-en-Provence`…).
 */
function studentCityKeyToLabel(cityKey: string): string {
  return cityKey
    .split('-')
    .map((segment: string): string =>
      segment
        .split(' ')
        .map((word: string): string => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' '),
    )
    .join('-')
}

/**
 * @param {GeoCoords} userCoords Coordonnées utilisateur.
 * @returns {string | undefined} Ville étudiante la plus proche (libellé affichable).
 */
function findClosestStudentCity(userCoords: GeoCoords): string | undefined {
  const candidates = Object.entries(STUDENT_CITY_COORDS)
    .map(([cityKey, coords]: [string, GeoCoords]) => ({
      city: studentCityKeyToLabel(cityKey),
      distanceKm: haversineDistanceKm(userCoords, coords),
    }))
    .sort((a, b) => a.distanceKm - b.distanceKm)

  return candidates[0]?.city
}

/**
 * @param {GeoCoords} from Coordonnées de départ.
 * @param {GeoCoords} to Coordonnées d'arrivée.
 * @returns {number} Distance en kilomètres.
 */
function haversineDistanceKm(from: GeoCoords, to: GeoCoords): number {
  const toRadians = (value: number): number => (value * Math.PI) / 180
  const earthRadiusKm = 6371
  const dLat = toRadians(to.lat - from.lat)
  const dLng = toRadians(to.lng - from.lng)
  const lat1 = toRadians(from.lat)
  const lat2 = toRadians(to.lat)
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) * Math.sin(dLng / 2)
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
  return earthRadiusKm * c
}

/**
 * @returns {string | undefined} Ville étudiante en cache si valide.
 */
function getCachedClosestStudentCity(): string | undefined {
  try {
    const raw = sessionStorage.getItem(CLOSEST_CITY_CACHE_KEY)
    if (!raw) return undefined

    const parsed = JSON.parse(raw) as CachedClosestCity
    const isFresh = Date.now() - parsed.timestamp < CLOSEST_CITY_CACHE_TTL_MS
    if (!isFresh) return undefined

    const cityKey = normalizeCityName(parsed.city)
    if (STUDENT_CITY_COORDS[cityKey] === undefined) return undefined

    return studentCityKeyToLabel(cityKey)
  } catch {
    return undefined
  }
}

/**
 * @param {string} city Ville étudiante à stocker en cache session.
 */
function setCachedClosestStudentCity(city: string): void {
  try {
    const payload: CachedClosestCity = { city, timestamp: Date.now() }
    sessionStorage.setItem(CLOSEST_CITY_CACHE_KEY, JSON.stringify(payload))
  } catch {
    console.error('Error setting cached closest student city to session storage')
  }
}

/**
 * @returns {Promise<GeoCoords | undefined>} Coordonnées via navigateur.
 */
async function getBrowserCoordinates(): Promise<GeoCoords | undefined> {
  if (!('geolocation' in navigator)) return undefined

  return new Promise<GeoCoords | undefined>((resolve) => {
    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve({
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        })
      },
      () => resolve(undefined),
      {
        enableHighAccuracy: false,
        timeout: 3000,
        maximumAge: 10 * 60 * 1000,
      },
    )
  })
}

/**
 * @returns {Promise<GeoCoords | undefined>} Coordonnées approximatives via IP.
 */
const ESTABLISHMENT_GEO_LABELS_CACHE: Map<string, EstablishmentSearchGeoLabels> = new Map()

interface NominatimReverseResponse {
  address?: {
    city?: string
    town?: string
    village?: string
    municipality?: string
    state?: string
    region?: string
    country?: string
  }
}

/**
 * Centroïde d'une bbox carte (recherche établissement).
 * @param {EstablishmentSearchBBox} bbox - Zone active.
 * @returns {GeoCoords} Point central.
 */
export function bboxToCenterCoords(bbox: EstablishmentSearchBBox): GeoCoords {
  return {
    lat: (bbox.southWestLat + bbox.northEastLat) / 2,
    lng: (bbox.southWestLng + bbox.northEastLng) / 2,
  }
}

/**
 * Bbox carrée approximative autour d'un point (≈ `radiusKm` de demi-côté).
 * @param {GeoCoords} center - Centre de la zone.
 * @param {number} radiusKm - Demi-côté en kilomètres.
 * @returns {EstablishmentSearchBBox} Bbox englobante.
 */
export function bboxAroundCoords(center: GeoCoords, radiusKm: number): EstablishmentSearchBBox {
  const latDelta: number = radiusKm / 111
  const lngDelta: number = radiusKm / (111 * Math.cos((center.lat * Math.PI) / 180))
  return {
    southWestLat: center.lat - latDelta,
    southWestLng: center.lng - lngDelta,
    northEastLat: center.lat + latDelta,
    northEastLng: center.lng + lngDelta,
  }
}

/**
 * Bbox locale autour de la position approximative de l'utilisateur (recherche établissement).
 * @param {number} [radiusKm] - Rayon de la zone en km (défaut 80).
 * @returns {Promise<EstablishmentSearchBBox | undefined>} Bbox ou `undefined` si position inconnue.
 */
export const getUserSearchBBox = async (
  radiusKm: number = 80,
): Promise<EstablishmentSearchBBox | undefined> => {
  const coords: GeoCoords | undefined = await getUserCoordinates()
  if (!coords) return undefined
  return bboxAroundCoords(coords, radiusKm)
}

/**
 * Reverse geocode Nominatim : coordonnées → villes / régions / pays lisibles PostHog.
 * Cache en mémoire par coordonnées arrondies (~11 km) pour limiter les appels.
 * @param {number} lat - Latitude.
 * @param {number} lng - Longitude.
 * @returns {Promise<EstablishmentSearchGeoLabels>} Labels géographiques.
 */
export async function coordsToEstablishmentSearchGeoLabels(
  lat: number,
  lng: number,
): Promise<EstablishmentSearchGeoLabels> {
  const cacheKey: string = `${lat.toFixed(1)},${lng.toFixed(1)}`
  const cached: EstablishmentSearchGeoLabels | undefined =
    ESTABLISHMENT_GEO_LABELS_CACHE.get(cacheKey)
  if (cached) return cached

  const emptyLabels: EstablishmentSearchGeoLabels = {
    countries: [],
    regions: [],
    cities: [],
  }

  try {
    const params: URLSearchParams = new URLSearchParams({
      format: 'json',
      lat: String(lat),
      lon: String(lng),
      zoom: '10',
      addressdetails: '1',
    })
    const response: Response = await fetch(
      `https://nominatim.openstreetmap.org/reverse?${params.toString()}`,
      { headers: { 'Accept-Language': 'fr' } },
    )
    if (!response.ok) return emptyLabels

    const payload = (await response.json()) as NominatimReverseResponse
    const address = payload.address
    if (!address) return emptyLabels

    const city: string | undefined =
      address.city?.trim() ||
      address.town?.trim() ||
      address.village?.trim() ||
      address.municipality?.trim() ||
      undefined
    const region: string | undefined = address.state?.trim() || address.region?.trim() || undefined
    const country: string | undefined = address.country?.trim() || undefined

    const labels: EstablishmentSearchGeoLabels = {
      countries: country ? [country] : [],
      regions: region ? [region] : [],
      cities: city ? [city] : [],
      ...(city ? { primary_city: city } : {}),
      ...(region ? { primary_region: region } : {}),
      ...(country ? { primary_country: country } : {}),
    }

    ESTABLISHMENT_GEO_LABELS_CACHE.set(cacheKey, labels)
    return labels
  } catch (error) {
    console.error('[geocoding] Reverse geocode échoué :', lat, lng, error)
    return emptyLabels
  }
}

/**
 * @param {EstablishmentSearchBBox} bbox - Zone carte active.
 * @returns {Promise<EstablishmentSearchGeoLabels>} Labels du centroïde.
 */
export async function bboxToEstablishmentSearchGeoLabels(
  bbox: EstablishmentSearchBBox,
): Promise<EstablishmentSearchGeoLabels> {
  const center: GeoCoords = bboxToCenterCoords(bbox)
  return coordsToEstablishmentSearchGeoLabels(center.lat, center.lng)
}

async function getIpCoordinates(): Promise<GeoCoords | undefined> {
  try {
    const response = await fetch('https://ipapi.co/json/')
    if (!response.ok) return undefined
    const payload = (await response.json()) as { latitude?: number; longitude?: number }
    console.log('payload', payload)
    if (typeof payload.latitude !== 'number' || typeof payload.longitude !== 'number') {
      return undefined
    }
    return {
      lat: payload.latitude,
      lng: payload.longitude,
    }
  } catch {
    return undefined
  }
}
